import { mdContentToHtml, replaceGptText } from '@op/utils/text';
import { useMemo, useRef, useState } from 'react';
import { getLocale } from 'umi';
import { LocaleType } from '@op/types/common';
import { GPTItemType } from '../types';

const statusMap: {
  [key in 'loading' | 'cancel']: { [key in LocaleType]: string };
} = {
  loading: {
    'zh-CN': '加载中。。。',
    'en-US': 'Loading...',
  },
  cancel: { 'zh-CN': '已取消', 'en-US': 'Cancel' },
};

export function dealwithDefaultGptText(
  gptText: string,
  otherObj: { [key: string]: any } = {},
): Array<GPTItemType> { 
  const replacedSummary = replaceGptText(gptText || "");
  const { html } = mdContentToHtml(replacedSummary);

  return [
    {
      'zh-CN': '',
      'en-US': '',
      renderHtml: html,
      status: gptText?.length < 20 ? 'failed' : 'success',
      summary: replacedSummary,
      ...otherObj,
    },
  ];
}

export function dealwithGptText(
  list: Array<GPTItemType> | null,
  {
    isDefault,
    getTitle = (item: GPTItemType) => item['en-US'],
  }: { isDefault?: boolean; getTitle?: (item: GPTItemType) => string },
) {
  let renderHtml = '';
  let summary = '';
  const statusList = list?.map((item) => {
    const {
      status,
      renderHtml: localRenderHtml,
      summary: localSummary,
    } = item || {};
    if (status !== 'failed') {
      const title = getTitle(item);
      if (title) {
        renderHtml += `<div class="dbo-aigc-card-summary-item-title">${title}</div>`;
        summary += `### ${title} \n`;
      }

      if (status === 'success') {
        renderHtml += localRenderHtml;
        summary += localSummary + '\n';
      } else {
        const lang: LocaleType = getLocale();
        const statusText = statusMap[status][lang];
        renderHtml += statusText;
        summary += statusText + '\n';
      }
    }

    return item.status;
  });
  const isLoading = statusList?.every((item) => item === 'loading');
  const isEmpty = statusList?.every((item) => item === 'failed');

  return {
    isEmpty: !!isEmpty,
    renderHtml: renderHtml,
    summary: summary,
    status: isLoading ? 'loading' : 'success',
    showDefaultGpt: isDefault,
  };
}

export default function useMultipleGpt({
  defaultGptText,
}: {
  defaultGptText?: string;
}) {
  const isDefault = useRef(true);
  const [allGptMap, setAllGptMap] = useState<{ [key: string]: GPTItemType }>(
    {},
  );
  const lang: LocaleType = getLocale();

  function getTitle(titleMap: { 'zh-CN': string; 'en-US': string }) {
    return titleMap[lang];
  }

  const defaultRenderInfo = useMemo(() => {
    if (!defaultGptText) return null;
    isDefault.current = true;
    return dealwithDefaultGptText(defaultGptText);
  }, [defaultGptText]);

  const { isEmpty, renderHtml, summary, status, showDefaultGpt } =
    useMemo(() => {
      const list = isDefault.current
        ? defaultRenderInfo
        : Object.values(allGptMap);

      return dealwithGptText(list, { isDefault: isDefault.current, getTitle });
    }, [defaultRenderInfo, allGptMap]);

  function changeAllGptMap(key: string, value: GPTItemType) {
    isDefault.current = false;
    setAllGptMap((prev) => ({ ...prev, [key]: value }));
  }

  return {
    status,
    changeAllGptMap,
    renderHtml,
    isEmpty,
    summary,
    getTitle,
    showDefaultGpt,
  };
}
